import { DropdownActionItem, DropdownActions } from '../../../../../../widgets/dropdown';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC, useRef } from 'react';
import { T } from 'src/shared/helpers';
import DragIcon from 'src/shared/material_icons/action/drag_indicator.svg';
import GroupIcon from 'src/shared/material_icons/action/join_inner.svg';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import RemoveIcon from 'src/shared/material_icons/action/delete_outline.svg';
import LinkIcon from 'src/shared/material_icons/content/link.svg';
import DuplicateIcon from 'src/shared/material_icons/content/content_copy.svg';
import PreSetIcon from 'src/shared/material_icons/content/inventory_2.svg';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import StepIcon from 'src/shared/material_icons/maps/layers.svg';
import ChevronRightIcon from 'src/shared/material_icons/navigation/chevron_right.svg';
import ChevronDownIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import RetargetIcon from 'src/shared/material_icons/maps/my_location.svg';
import styled, { css } from 'styled-components';
import { ComposerRowActionButton as RowActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { ComposerAttributeNode, ComposerAttributeType } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getComposerAttributeValidationMessages } from '../helpers/composerAttributeValidation';
import { setComposerRowDragImage } from '../helpers/composerDragImage';
import { ComposerAttributeDropPosition } from '../helpers/composerTreeDnD';
import ComposerValidationTooltip from './composerValidationTooltip';

type ComposerRowAction = 'links' | 'duplicate' | 'retarget' | 'remove';

interface ComposerAttributeTreeElementContextMenuData {
    onContextMenuItemClick: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    contextMenu: React.ReactNode;
}

interface ComposerAttributeTreeElementProps {
    attribute: ComposerAttributeNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onToggleExpand?: (attribute: ComposerAttributeNode) => void;
    onMoveUp?: (attribute: ComposerAttributeNode) => void;
    onMoveDown?: (attribute: ComposerAttributeNode) => void;
    onDragStart?: (event: React.DragEvent<HTMLButtonElement>, attribute: ComposerAttributeNode) => void;
    onDragEnd?: (event: React.DragEvent<HTMLButtonElement>, attribute: ComposerAttributeNode) => void;
    onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragLeave?: (event: React.DragEvent<HTMLDivElement>) => void;
    onAction?: (action: ComposerRowAction, attribute: ComposerAttributeNode) => void;
    onVisibilityClick?: (attribute: ComposerAttributeNode) => void;
    contextMenu?: ComposerAttributeTreeElementContextMenuData;
    selected: boolean;
    hasLink?: boolean;
    expanded?: boolean;
    dragging?: boolean;
    dropPosition?: ComposerAttributeDropPosition;
    shift?: number;
    type: ComposerAttributeType;
    children?: React.ReactNode;
}

type ComposerAttributeSubTreeElementProps = Omit<ComposerAttributeTreeElementProps, 'type' | 'children' | 'expanded'>;

const Row = styled.div<{
    $shift: number;
    $selected: boolean;
    $dragging: boolean;
    $dropPosition?: ComposerAttributeDropPosition;
}>`
    position: relative;
    display: flex;
    min-width: 0;
    height: 34px;
    align-items: center;
    gap: 5px;
    margin-left: ${(props) => props.$shift * 18}px;
    padding: 0 6px;
    color: var(--composer-fg, #1e2a38);
    background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'transparent')};
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    outline: none;
    user-select: none;
    opacity: ${(props) => (props.$dragging ? 0.45 : 1)};
    transition:
        opacity 110ms ease,
        background 110ms ease,
        box-shadow 110ms ease;

    ${(props) =>
        props.$dropPosition === 'inside' &&
        css`
            background: var(--composer-accent-wash, #fdf0e6);
            box-shadow: inset 0 0 0 2px var(--composer-accent, #e8650a);
        `}

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'var(--composer-hover, #e9eff5)')};
    }

    &::before {
        position: absolute;
        top: 6px;
        bottom: 6px;
        left: 0;
        display: ${(props) => (props.$selected ? 'block' : 'none')};
        width: 2.5px;
        background: var(--composer-accent, #e8650a);
        border-radius: 2px;
        content: '';
    }

    &::after {
        position: absolute;
        right: 6px;
        ${(props) => (props.$dropPosition === 'before' ? 'top: -3px;' : 'bottom: -3px;')}
        left: 6px;
        z-index: 2;
        display: ${(props) => (props.$dropPosition === 'before' || props.$dropPosition === 'after' ? 'block' : 'none')};
        height: 3px;
        background: var(--composer-accent, #e8650a);
        border-radius: 999px;
        box-shadow: 0 0 0 1px var(--composer-panel, #fff);
        content: '';
        pointer-events: none;
    }

    /* Row commands appear on hover, except the ones marked persistent: a constraint and a disabled row must read at a glance. */
    > div:last-child > *:not([data-persistent]) {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    /* :focus-within alone would keep them lit after a plain click, since the row itself takes focus. */
    &:hover > div:last-child > *,
    &:focus-within:not(:focus) > div:last-child > * {
        opacity: 1;
    }

    ${(props) =>
        props.$shift > 0 &&
        css`
            width: calc(100% - ${props.$shift * 18}px);
        `}
`;

const RowMain = styled.div<{ $muted: boolean }>`
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 7px;
    opacity: ${(props) => (props.$muted ? 0.5 : 1)};
`;

const RowName = styled.span`
    min-width: 0;
    flex: 0 1 auto;
    overflow: hidden;
    font-size: 12.5px;
    font-weight: 500;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const RowCount = styled.span`
    flex: 0 0 auto;
    color: var(--composer-faint, #677078);
    font-size: 10px;
    font-variant-numeric: tabular-nums;
`;

const RowActions = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 1px;
`;

const RowIcon = styled.span<{ $kind: 'group' | 'attribute' | 'option' | 'neutral' }>`
    display: inline-flex;
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    color: ${(props) => {
        if (props.$kind === 'group') return 'var(--composer-kind-group, #7040d0)';
        if (props.$kind === 'option') return 'var(--composer-kind-option, #b32d7d)';
        if (props.$kind === 'attribute') return 'var(--composer-kind-attribute, #0e7c7c)';
        return 'var(--composer-muted, #5c6b7a)';
    }};

    svg {
        width: 17px;
        height: 17px;
        fill: currentColor;
    }
`;

const Slot = styled.span`
    display: inline-flex;
    width: 28px;
    height: 28px;
`;

const Children = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const getKind = (type: ComposerAttributeType) => {
    if (type === ComposerAttributeType.Attribute) return 'attribute' as const;
    if (type === ComposerAttributeType.AttributeGroup) return 'group' as const;
    return 'neutral' as const;
};

const getTypeLabel = (type: ComposerAttributeType) => {
    if (type === ComposerAttributeType.Attribute) return T._('Attribute', 'Admin');
    if (type === ComposerAttributeType.AttributeGroup) return T._('Group', 'Admin');
    if (type === ComposerAttributeType.AttributeStep) return T._('Step', 'Admin');
    return T._('Preset', 'Admin');
};

const getTypeIcon = (type: ComposerAttributeType) => {
    if (type === ComposerAttributeType.Attribute) return <AttributeIcon />;
    if (type === ComposerAttributeType.AttributeGroup) return <GroupIcon />;
    if (type === ComposerAttributeType.AttributeStep) return <StepIcon />;
    return <PreSetIcon />;
};

const getMenuDescriptions = (type: ComposerAttributeType) => {
    if (type === ComposerAttributeType.AttributeGroup) {
        return {
            duplicate: T._('A copy with its attributes', 'Admin'),
            remove: T._('The group and its attributes', 'Admin')
        };
    }
    if (type === ComposerAttributeType.Attribute) {
        return {
            duplicate: T._('A copy with its options', 'Admin'),
            remove: T._('The attribute and its options', 'Admin')
        };
    }

    const typeLabel = getTypeLabel(type).toLocaleLowerCase();
    return {
        duplicate: T._('A copy with its content', 'Admin'),
        remove: T._(`The ${typeLabel} and its content`, 'Admin')
    };
};

export const ComposerAttributeTreeElement: FC<ComposerAttributeTreeElementProps> = ({
    attribute,
    onSelect,
    onDoubleClick,
    onToggleExpand,
    contextMenu,
    selected,
    hasLink,
    shift = 0,
    expanded,
    dragging = false,
    dropPosition,
    type,
    children,
    onMoveUp,
    onMoveDown,
    onDragStart,
    onDragEnd,
    onDrop,
    onDragOver,
    onDragLeave,
    onAction,
    onVisibilityClick
}) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const validationMessages = getComposerAttributeValidationMessages(attribute);
    const hasWarnings = validationMessages.length > 0;
    const itemCount = type === ComposerAttributeType.Attribute ? attribute.options.length : React.Children.count(children);
    const menuDescriptions = getMenuDescriptions(type);
    const enabled = attribute.enabled !== false;

    return (
        <>
            <Row
                ref={rowRef}
                $shift={shift}
                $selected={selected}
                $dragging={dragging}
                $dropPosition={dropPosition}
                aria-expanded={children ? !expanded : undefined}
                aria-selected={selected}
                onClick={(event) => onSelect(event, attribute)}
                onContextMenu={(event) => {
                    if (!contextMenu) return;
                    event.preventDefault();
                    event.stopPropagation();
                    contextMenu.onContextMenuItemClick(event, attribute);
                }}
                onDoubleClick={onDoubleClick ? (event) => onDoubleClick(event, attribute) : undefined}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowUp' && event.altKey) {
                        event.preventDefault();
                        onMoveUp?.(attribute);
                    }
                    if (event.key === 'ArrowDown' && event.altKey) {
                        event.preventDefault();
                        onMoveDown?.(attribute);
                    }
                }}
                role='treeitem'
                tabIndex={0}
            >
                <RowMain $muted={!enabled}>
                    {children ? (
                        <ZTooltip title={!expanded ? T._('Collapse', 'Admin') : T._('Expand', 'Admin')}>
                            <RowActionButton
                                aria-label={!expanded ? T._('Collapse', 'Admin') : T._('Expand', 'Admin')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onToggleExpand?.(attribute);
                                }}
                            >
                                {!expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                            </RowActionButton>
                        </ZTooltip>
                    ) : (
                        <Slot />
                    )}
                    {hasWarnings && <ComposerValidationTooltip messages={validationMessages} />}
                    <ZTooltip title={getTypeLabel(type)}>
                        <RowIcon $kind={getKind(type)}>{getTypeIcon(type)}</RowIcon>
                    </ZTooltip>
                    <RowName title={attribute.name}>{attribute.name}</RowName>
                    <RowCount aria-label={T._(`${itemCount} items`, 'Admin')}>{itemCount}</RowCount>
                </RowMain>
                <RowActions>
                    {hasLink ? (
                        <ZTooltip title={T._('This item has constraints', 'Admin')}>
                            <RowActionButton
                                $active
                                aria-label={T._('Open constraints', 'Admin')}
                                data-persistent='true'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onAction?.('links', attribute);
                                }}
                            >
                                <LinkIcon />
                            </RowActionButton>
                        </ZTooltip>
                    ) : (
                        <Slot />
                    )}
                    <ZTooltip title={enabled ? T._('Disable this row', 'Admin') : T._('Enable this row', 'Admin')}>
                        <RowActionButton
                            $off={!enabled}
                            aria-label={enabled ? T._('Disable this row', 'Admin') : T._('Enable this row', 'Admin')}
                            aria-pressed={!enabled}
                            data-persistent={enabled ? undefined : 'true'}
                            onClick={(event) => {
                                event.stopPropagation();
                                onVisibilityClick?.(attribute);
                            }}
                        >
                            {enabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </RowActionButton>
                    </ZTooltip>
                    <DropdownActions
                        header={
                            <RowActionButton type='button' aria-label={T._('More commands', 'Admin')}>
                                <MoreIcon />
                            </RowActionButton>
                        }
                        items={[
                            <DropdownActionItem
                                key='duplicate'
                                caption={menuDescriptions.duplicate}
                                icon={<DuplicateIcon />}
                                onClick={() => onAction?.('duplicate', attribute)}
                            >
                                {T._('Duplicate', 'Admin')}
                            </DropdownActionItem>,
                            <DropdownActionItem
                                key='retarget'
                                caption={T._('Point the selectors at another model', 'Admin')}
                                disabled={type !== ComposerAttributeType.Attribute || attribute.options.length === 0}
                                icon={<RetargetIcon />}
                                onClick={() => onAction?.('retarget', attribute)}
                            >
                                {T._('Retarget', 'Admin')}
                            </DropdownActionItem>,
                            <DropdownActionItem
                                key='remove'
                                caption={menuDescriptions.remove}
                                danger
                                icon={<RemoveIcon />}
                                onClick={() => onAction?.('remove', attribute)}
                            >
                                {T._('Remove', 'Admin')}
                            </DropdownActionItem>
                        ]}
                        minWidth='260px'
                        orientation='RIGHT'
                    />
                    <ZTooltip title={T._('Drag to reorder · Alt+Arrow to move', 'Admin')}>
                        <RowActionButton
                            aria-label={T._('Drag to reorder', 'Admin')}
                            draggable={!!onDragStart}
                            onDragEnd={(event) => onDragEnd?.(event, attribute)}
                            onDragStart={(event) => {
                                event.stopPropagation();
                                setComposerRowDragImage(event.dataTransfer, rowRef.current);
                                onDragStart?.(event, attribute);
                            }}
                        >
                            <DragIcon />
                        </RowActionButton>
                    </ZTooltip>
                </RowActions>
            </Row>
            {!expanded && children && <Children role='group'>{children}</Children>}
            {contextMenu?.contextMenu}
        </>
    );
};

export const ComposerAttributeSubTreeElement: FC<ComposerAttributeSubTreeElementProps> = ({ attribute, shift = 1, ...props }) => (
    <ComposerAttributeTreeElement {...props} attribute={attribute} shift={shift} type={ComposerAttributeType.Attribute} />
);
